import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FolderKanban, Globe, KeyRound, Mail, Pencil, Phone, Plus, MapPin, StickyNote, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, eur, errMsg, fmtDateShort } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { ClientForm } from "../components/SimpleForms";
import { CredentialList } from "../components/CredentialList";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [{ data }, p, c] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get("/projects"),
        api.get("/clients"),
      ]);
      setClient(data);
      setProjects(p.data);
      setClients(c.data);
    } catch (e) {
      toast.error(errMsg(e));
      navigate("/kunden");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!client) return <div className="h-64 animate-pulse rounded-2xl bg-white" data-testid="client-detail-loading" />;

  const remove = async () => {
    if (!window.confirm(`Kunde „${client.company}" wirklich löschen?`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Kunde gelöscht");
      navigate("/kunden");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="animate-fade-up" data-testid="client-detail-page">
      <PageHeader overline="Kunde" title={client.company} testid="client-detail-header">
        <Button variant="outline" data-testid="client-edit-button" onClick={() => setFormOpen(true)} className="rounded-xl">
          <Pencil className="mr-1.5 h-4 w-4" /> Bearbeiten
        </Button>
        {user.role === "owner" && (
          <Button variant="outline" data-testid="client-delete-button" onClick={remove} className="rounded-xl border-red-200 text-red-600 hover:bg-red-50">
            Löschen
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 shadow-sm" data-testid="client-info-card">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#B88A4A]">Kontaktdaten</p>
            <div className="space-y-2.5 text-sm text-[#1D225C]">
              {client.contact_person && <p className="flex items-center gap-2"><Plus className="hidden" />{client.contact_person}</p>}
              {client.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#B88A4A]" /> <a className="hover:underline" href={`mailto:${client.email}`}>{client.email}</a></p>}
              {client.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#B88A4A]" /> {client.phone}</p>}
              {client.website && (
                <p className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-[#B88A4A]" />
                  <a className="text-[#B88A4A] hover:underline" target="_blank" rel="noreferrer" href={client.website.startsWith("http") ? client.website : `https://${client.website}`}>{client.website}</a>
                </p>
              )}
              {client.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#B88A4A]" /> {client.address}</p>}
            </div>
          </div>
          {client.notes && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="client-notes">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-amber-700"><StickyNote className="h-3.5 w-3.5" /> Notizen</p>
              <p className="whitespace-pre-wrap text-sm text-amber-900">{client.notes}</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 text-center shadow-sm">
              <FolderKanban className="mx-auto mb-1 h-4 w-4 text-[#B88A4A]" />
              <p className="font-display text-xl font-bold text-[#1D225C]">{client.projects.length}</p>
              <p className="text-[11px] text-[#777]">Projekte</p>
            </div>
            <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 text-center shadow-sm">
              <KeyRound className="mx-auto mb-1 h-4 w-4 text-[#B88A4A]" />
              <p className="font-display text-xl font-bold text-[#1D225C]">{client.credentials_count}</p>
              <p className="text-[11px] text-[#777]">Zugänge</p>
            </div>
            <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 text-center shadow-sm">
              <Wallet className="mx-auto mb-1 h-4 w-4 text-[#B88A4A]" />
              <p className="font-display text-xl font-bold text-[#1D225C]">{eur(client.total_paid_to_mondher)}</p>
              <p className="text-[11px] text-[#777]">An Mondher</p>
            </div>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <section data-testid="client-projects-section">
            <h3 className="mb-3 font-display text-base font-semibold text-[#1D225C]">Projekt-Historie</h3>
            {client.projects.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-[#1D225C]/15 bg-white/60 px-4 py-8 text-center text-sm text-[#777]">
                Noch keine Projekte für diesen Kunden.
              </p>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                {client.projects.map((p, i) => (
                  <button
                    key={p.id}
                    data-testid={`client-project-${p.id}`}
                    onClick={() => navigate(`/projekte/${p.id}`)}
                    className={`flex w-full flex-wrap items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#F7F6F2] ${i ? "border-t border-[#1D225C]/5" : ""}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[#1D225C]">{p.name}</span>
                      <span className="block text-xs text-[#777]">{p.website_url || "—"}{p.deadline ? ` · ${fmtDateShort(p.deadline)}` : ""}</span>
                    </span>
                    <StatusBadge status={p.status} />
                  </button>
                ))}
              </div>
            )}
          </section>

          <section data-testid="client-credentials-section">
            <h3 className="mb-3 font-display text-base font-semibold text-[#1D225C]">Zugänge des Kunden</h3>
            <CredentialList clientId={id} projects={projects} clients={clients} />
          </section>
        </div>
      </div>

      <ClientForm open={formOpen} onOpenChange={setFormOpen} client={client} onSaved={load} />
    </div>
  );
}
